import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { useShifts } from "../../../hooks/useShifts";
import { showAgencyPostingResult } from "../../../utils/agencyWarnings";
import { intendedWindowsForNewShift } from "../../../utils/incentive";
import type { IncentivePlan, NurseFullRole, NurseRole, OpenShiftRequest, PostShiftData, RequestSource } from "../../../utils/models";
import { isOpenShiftBelowMaximumDuration } from "../../../utils/openShiftDuration";
import { getOverlappingShift, getShiftIndexByShiftTimes } from "../../../utils/utilFunctions";
import { showConfirmationDialog } from "../../staffingSearch/CreateShiftContent";

type CheckForOverlapsAndMergeIsUserApprovedReturn = {
	oldNurseAmount: number;
} | null;

export const useCheckOverlappingShiftsAndPatch = () => {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	const { refreshRequests, staffRequests } = useRequestsContext();
	const { isLoading, patchShift, isPatchShiftSuccess, postShift } = useShifts();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const { t } = useTranslation();

	const handleOverlappingShift = async ({
		oldNeededNurses,
		updatedNeededNurses,
		shiftId,
		shiftEnd,
		shiftStart,
		actionAfterMerge,
		actionIfMergeCanceled,
	}: {
		shiftId: string;
		oldNeededNurses: number;
		updatedNeededNurses: number;
		shiftStart: DateTime;
		shiftEnd: DateTime;
		actionAfterMerge?: (result: CheckForOverlapsAndMergeIsUserApprovedReturn) => void;
		actionIfMergeCanceled?: () => void;
	}): Promise<void> => {
		let dialogResult = "";
		await showConfirmationDialog({
			title: "Overlapping Open Shift",
			content: "A similar open shift already exists. Would you like to update the needed nurses?",
			actions: [
				{
					label: `Keep (${oldNeededNurses})`,
					onClick: () => {
						if (actionIfMergeCanceled) {
							actionIfMergeCanceled();
						}
					},
				},
				{
					label: `Merge (${updatedNeededNurses + oldNeededNurses})`,
					onClick: () => {
						dialogResult = "merge";
					},
				},
			],
		});

		if (dialogResult === "merge") {
			try {
				if (isLoading.patchShift) return;

				const patchResult = await patchShift({
					shiftId,
					neededNurses: updatedNeededNurses + oldNeededNurses,
				});
				showAgencyPostingResult(showSnackbar, t("success.needed.nurses.updated"), t, patchResult?.agency_posting_failures);
				await refreshRequests();
				if (actionAfterMerge) {
					actionAfterMerge({
						oldNurseAmount: oldNeededNurses,
					});
				}
			} catch (error) {
				showErrorSnackbar(
					"Failed to update needed nurses",
					{
						action: "update needed nurses open shift failed",
						shift_start: shiftStart,
						shift_end: shiftEnd,
					},
					error,
				);
			}
		}
	};

	const checkForOverlapsAndMergeIfUserApproved = async ({
		shiftStart,
		shiftEnd,
		selectedRoles,
		jobId,
		actionsWhenMergeNotNeeded,
		neededNurses,
		actionAfterMerge,
		actionIfMergeCanceled,
		chargeOnly = false,
		bonus = 0,
		panel,
		isMasterSchedule,
		location,
		agencyShiftIds,
		postToAgency,
		postWithDelay,
		agencyPostDelayHours,
		agencyLicenseTypes,
		agencyFacilityIds,
		agencySource,
		agencySourceName,
		requestSource,
		subRole,
		incentiveConfigurationId,
		incentiveConfigurationPlans,
	}: {
		shiftStart: DateTime;
		shiftEnd: DateTime;
		selectedRoles: Array<NurseFullRole>;
		jobId?: string;
		neededNurses: number;
		chargeOnly?: boolean;
		bonus?: number;
		actionsWhenMergeNotNeeded?: {
			onSuccess?: () => void;
			onError?: () => void;
		};
		actionAfterMerge?: (result: CheckForOverlapsAndMergeIsUserApprovedReturn) => void;
		actionIfMergeCanceled?: () => void;
		/** Where the post was made, for the `post_shift*` analytics. `gaps-sheet` is the gaps-first home's post sheet. */
		panel: "create-desktop" | "create-mobile" | "quick-post" | "profile-deactivation" | "gaps-sheet";
		isMasterSchedule?: boolean;
		location?: string;
		agencyShiftIds?: string[];
		postToAgency?: boolean;
		postWithDelay?: boolean;
		agencyPostDelayHours?: number;
		agencyLicenseTypes?: string[];
		agencyFacilityIds?: string[];
		agencySource?: string[];
		agencySourceName?: string;
		requestSource?: RequestSource;
		subRole?: NurseRole;
		incentiveConfigurationId?: string;
		incentiveConfigurationPlans?: IncentivePlan[];
	}): Promise<CheckForOverlapsAndMergeIsUserApprovedReturn> => {
		if (!isOpenShiftBelowMaximumDuration(shiftStart, shiftEnd)) {
			showSnackbar(t("error.open.shift.duration.too.long"), "error");
			actionsWhenMergeNotNeeded?.onError?.();
			return null;
		}
		const existingSimilarOpenShift = getOverlappingShift({
			staffRequests,
			startTime: shiftStart,
			endTime: shiftEnd,
			roles: selectedRoles,
			jobManagementEnabled,
			jobId,
			intendedBonusWindows: intendedWindowsForNewShift(bonus, incentiveConfigurationPlans),
			chargeOnly,
			location,
			postToAgency,
			subRole,
			agencySourceName,
		});
		if (existingSimilarOpenShift) {
			const oldAmount = (existingSimilarOpenShift as OpenShiftRequest).needed_nurses;
			await handleOverlappingShift({
				shiftId: existingSimilarOpenShift.id,
				shiftStart,
				shiftEnd,
				oldNeededNurses: oldAmount,
				updatedNeededNurses: neededNurses,
				actionAfterMerge,
				actionIfMergeCanceled,
			});
			return {
				oldNurseAmount: oldAmount,
			};
		}

		try {
			const shiftIndex: number = getShiftIndexByShiftTimes({
				startTime: shiftStart,
				endTime: shiftEnd,
			});
			track("post_shift", {
				roles: JSON.stringify(selectedRoles),
				needed_nurses: neededNurses,
				start_time: shiftStart.toISO(),
				end_time: shiftEnd.toISO(),
				department_id: chosenDepartment.department_id,
				shift_index: shiftIndex,
				panel: panel,
				source: isMasterSchedule ? "master_schedule" : "staffing",
				...(location
					? {
							location: location,
						}
					: {}),
			});

			const postShiftData: PostShiftData = {
				roles: selectedRoles,
				jobId: jobId,
				neededNurses: neededNurses,
				startTime: shiftStart,
				endTime: shiftEnd,
				shiftNo: shiftIndex,
				departmentId: chosenDepartment.department_id,
				bonus: bonus,
				chargeOnly: chargeOnly,
				location,
				agencyShiftIds: agencyShiftIds,
				postToAgency: postToAgency,
				postWithDelay,
				agencyPostDelayHours,
				agencyLicenseTypes: agencyLicenseTypes,
				agencyFacilityIds: agencyFacilityIds,
				agencySource: agencySource,
				agencySourceName: agencySourceName,
				source: requestSource,
				subRole: subRole,
				incentiveConfigurationId,
			};

			const postedRequest = await postShift({ ...postShiftData, isMasterSchedule });
			await refreshRequests();

			// Non-fatal agency-write failures: the IHH shift was posted, but the agency sync warned.
			showAgencyPostingResult(showSnackbar, t("shift.posted.successfully"), t, postedRequest?.agency_posting_failures);
			track("post_shift_success", {
				roles: JSON.stringify(selectedRoles),
				needed_nurses: neededNurses,
				start_time: shiftStart.toISO(),
				end_time: shiftEnd.toISO(),
				department_id: chosenDepartment.department_id,
				shift_index: shiftIndex,
				panel: panel,
				...(location
					? {
							location: location,
						}
					: {}),
			});
			if (actionsWhenMergeNotNeeded?.onSuccess) {
				actionsWhenMergeNotNeeded.onSuccess();
			}
		} catch (e) {
			showErrorSnackbar("Failed to post shift", { date: shiftStart.toISO() }, e);
			trackError("post_shift_error", {
				roles: JSON.stringify(selectedRoles),
				needed_nurses: neededNurses,
				start_time: shiftStart.toISO(),
				end_time: shiftEnd.toISO(),
				department_id: chosenDepartment.department_id,
				panel: panel,
				...(location
					? {
							location: location,
						}
					: {}),
			});
			if (actionsWhenMergeNotNeeded?.onError) {
				actionsWhenMergeNotNeeded.onError();
			}
		}

		return null;
	};

	return {
		checkForOverlapsAndMergeIfUserApproved,
		patchLoading: isLoading.patchShift,
		postLoading: isLoading.postShift,
		isPatchShiftSuccess,
	};
};
